<template>
  <div class="context-select">
    <SplitButton align="end" variant="primary" size="small" outlined>
      <template #action>
        <BaseButton
          v-if="hasContextSet"
          v-tooltip="{
            content: `
            Current context is the <b>${contextModelName}</b>
            <em>${contextItemName}</em>. Click to remove context`,
            html: true,
          }"
          :icon="hoverButton ? ['fa', 'fa-times'] : modelIconClasses"
          size="small"
          :variant="hoverButton ? 'danger' : 'primary'"
          :label="contextItemName"
          class="context-action-btn"
          @click="() => $emit('unset-context')"
          @mouseover="hoverButton = true"
          @mouseleave="hoverButton = false"
        />
        <BaseButton
          v-else
          :icon="['fa', 'fa-cube']"
          size="small"
          variant="primary"
          :outlined="true"
          label="Context"
          disabled
          class="context-action-btn"
        />
      </template>
      <template #default>
        <div style="min-width: 275px">
          <div v-if="hasContextSet" class="dd-section">
            <div class="dd-section-label">Current context</div>
            <div class="dd-item context-info">
              <span class="icon has-text-primary">
                <i
                  :class="
                    iconClasses(contextAppName, contextPluralisedModelName)
                  "
                />
              </span>
              <div>
                <p class="has-text-weight-semibold">{{ contextItemName }}</p>
                <p class="is-size-7 has-text-grey">
                  {{ contextModelName }} [{{
                    $filters.lowercase(contextModelType)
                  }}]
                </p>
              </div>
            </div>
            <a
              class="dd-item has-text-danger"
              @click="() => $emit('unset-context')"
            >
              <span class="icon">
                <i class="fa-solid fa-times" />
              </span>
              Remove context
            </a>
          </div>
          <div class="dd-section">
            <div class="dd-section-label">Set context</div>
            <a
              v-for="{ appName, modelName, modelType } in contextModelChoices"
              :key="`opt-${appName}${modelName}${modelType}`"
              class="dd-item"
              @click="selectModel({ appName, modelName, modelType })"
            >
              <span class="icon">
                <i :class="iconClasses(appName, modelName)" />
              </span>
              {{ $filters.capitalizeWords(modelNameSingularize(modelName)) }}
            </a>
          </div>
        </div>
      </template>
    </SplitButton>
  </div>
</template>

<script>
import { get as _get } from 'lodash-es'
import {
  modelNamePluralize,
  modelNameSingularize,
  getModelIconClasses,
} from '@/utils'

export default {
  name: 'ContextSetter',
  emits: ['unset-context', 'select-context-model'],
  props: {
    contextModelChoices: {
      type: Array,
      default: () => [],
    },
    context: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      hoverButton: false,
    }
  },
  computed: {
    hasContextSet() {
      return this.context && this.contextItemId
    },
    contextItemId() {
      return _get(this.context, 'id.value') || this.context.id
    },
    contextItemName() {
      return _get(this.context, 'name.value.name', this.contextItemId)
    },
    contextAppName() {
      return (this.hasContextSet && this.context.appName) || ''
    },
    contextModelName() {
      return (this.hasContextSet && this.context.modelName) || ''
    },
    contextModelType() {
      return (this.hasContextSet && this.context.modelType) || ''
    },
    contextPluralisedModelName() {
      return modelNamePluralize(this.contextModelName)
    },
    modelIconClasses() {
      let iconClasses = ['fa', 'fa-cube']
      if (this.contextAppName && this.contextPluralisedModelName) {
        iconClasses = this.iconClasses(
          this.contextAppName,
          this.contextPluralisedModelName,
        )
      }
      return iconClasses
    },
  },
  methods: {
    modelNameSingularize,
    iconClasses: getModelIconClasses,
    selectModel(m) {
      this.$emit('select-context-model', m)
    },
  },
}
</script>

<style lang="scss" scoped>
.context-action-btn {
  max-width: 200px;
  overflow: hidden;

  :deep(.btn__label) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.context-info {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  cursor: default;

  &:hover {
    background: transparent;
  }
}
</style>
